import { useFormContext, useWatch } from 'react-hook-form'
import { Info } from 'lucide-react'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { CampaignFormInput } from './schema'

export function CampaignRewards() {
  const form = useFormContext<CampaignFormInput>()
  const rewardType = useWatch({ control: form.control, name: 'rewardType' })

  return (
    <>
      {/* Recompensas */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Regras de Recompensa</h3>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="rewardType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo de Recompensa</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentagem (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="rewardValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Valor da Recompensa</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      <Separator />

      {/* Condições e Limites */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Condições e Limites</h3>
        <div className="grid grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="warrantyDays"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Dias de Carência</FormLabel>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-[300px]">
                        <p>
                          Período de segurança antes do saldo ficar disponível. Evita fraudes (ex:
                          compra, resgate imediato e cancelamento).
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <FormControl>
                  <Input type="number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="minOrderValue"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mínimo (R$)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {rewardType === 'percentage' && (
            <FormField
              control={form.control}
              name="maxRewardValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Teto Recomp. (R$)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Ilimitado"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Fraud Protection */}
        <FormField
          control={form.control}
          name="autoRefundFraud"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border border-red-100 bg-red-50/50 p-4 shadow-sm">
              <div className="space-y-0.5">
                <FormLabel className="text-base font-medium text-red-900">
                  Cancelamento Automático de Fraudes
                </FormLabel>
                <FormDescription className="text-red-700 text-xs">
                  Se ativado, pedidos identificados como fraude (auto-indicação) serão cancelados
                  automaticamente na plataforma externa (Guru/Yampi).
                </FormDescription>
              </div>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </>
  )
}
